<template>
    <v-card>
        <v-card-title>
            {{editFormTitle}}
            <v-spacer></v-spacer>
            <v-btn icon @click="close"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
            <v-container class="flex-column">
                <v-row>
                    <v-col cols="12" sm="6">
                        <v-text-field
                            v-model="editedItem.id"
                            dense
                            hide-details
                            label="Код скважины"
                        ></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6">
                        <v-select
                            v-model="editedItem.type"
                            :items="types"
                            dense
                            hide-details
                            label="Тип скважины"
                        >
                        </v-select>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" sm="6">
                        <v-text-field
                            v-model="editedItem.depth"
                            type="number"
                            dense
                            hide-details
                            label="Глубина"
                        >
                            <template #append>м</template>
                        </v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6">
                        <v-text-field
                            v-model="editedItem.bottomhole_depth"
                            type="number"
                            dense
                            hide-details
                            label="Глубина забоя"
                        >
                            <template #append>м</template>
                        </v-text-field>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" sm="6" md="4">
                        <v-text-field
                            v-model.number="editedItem.x_coord"
                            min="0"
                            :max="reservoir.grids_x * reservoir.dx"
                            dense
                            hide-details
                            type="number"
                            label="X"
                        >
                            <template #append>м</template>
                        </v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                        <v-text-field
                            v-model.number="editedItem.y_coord"
                            min="0"
                            :max="reservoir.grids_y * reservoir.dy"
                            dense
                            hide-details
                            type="number"
                            label="Y"
                        >
                            <template #append>м</template>
                        </v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                        <v-text-field
                            v-model.number="editedItem.z_coord_relative"
                            min="0"
                            :max="reservoir.grids_z * reservoir.dz"
                            dense
                            hide-details
                            type="number"
                            label="Z (относительная)"
                        >
                            <template #append>м</template>
                        </v-text-field>
                        {{editedItem ? editedItem.z_coord : ""}}
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" sm="6" md="4">
                        <v-text-field
                            v-model.number="editedItem.k_well_permeability"
                            min="0"
                            dense
                            hide-details
                            type="number"
                            label="k"
                        >
                            <template #append>мД</template>
                        </v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                        <v-text-field
                            v-model.number="editedItem.phi_well_porosity"
                            min="0"
                            dense
                            hide-details
                            type="number"
                        >
                            <template #label>&phi;</template>
                        </v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                        <v-text-field
                            v-model.number="editedItem.sw_water_saturation"
                            min="0" max="1" step="0.1"
                            dense
                            hide-details
                            type="number"
                        >
                            <template #label>S<sub>w</sub></template>
                        </v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                        <v-text-field
                            v-model.number="editedItem.p_pressure"
                            min="0"
                            dense
                            hide-details
                            type="number"
                            label="p"
                        >
                            <template #append>бар</template>
                        </v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                        <v-text-field
                            v-model.number="editedItem.q_initial_rate"
                            dense
                            hide-details
                            type="number"
                            label="Начальный дебит, q"
                        >
                            <template #append><nobr>м3/д</nobr></template>
                        </v-text-field>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12">
                        <v-card elevation="0">
                            <v-card-title>
                                Перфорации
                                <v-spacer></v-spacer>
                                <v-btn color="primary" elevation="0" @click="joinNodes" class="mr-2">Объединить</v-btn>
                                <v-btn color="primary" elevation="0" @click="addNode">Добавить</v-btn>
                            </v-card-title>
                            <v-expansion-panels>
                                <v-expansion-panel v-for="(node, i) in editedItem.nodes" :key="i">
                                    <v-expansion-panel-header>
                                        <span>Перфорация #{{i+1}}</span>
                                        <v-spacer></v-spacer>
                                        <div>
                                            <v-btn icon small v-if="filterConnectionsNodes.includes(i)" color="success"
                                                @click.stop="filterConnectionsNodes.splice(filterConnectionsNodes.indexOf(i), 1);"
                                            ><v-icon small>mdi-filter-remove</v-icon></v-btn>
                                            <v-btn icon small v-else
                                                @click.stop="filterConnectionsNodes.push(i)"
                                            ><v-icon small>mdi-filter-plus</v-icon></v-btn>
                                        </div>
                                    </v-expansion-panel-header>
                                    <v-expansion-panel-content>
                                        <node-edit v-model="editedItem.nodes[i]" :reservoir="reservoir"></node-edit>
                                    </v-expansion-panel-content>
                                </v-expansion-panel>
                            </v-expansion-panels>
                        </v-card>
                    </v-col>
                </v-row>

                <v-row>
                    <v-col cols="12">
                        <v-select
                            :value="getConnectionsValue(editedItem)"
                            :items="getAllConnections(editedItem)"
                            label="Соединения"
                            multiple
                            chips
                            deletable-chips
                            @input="setConnections"
                        >
                            <template #append v-if="filterConnectionsNodes && filterConnectionsNodes.length > 0">
                                <v-btn icon small @click.stop="filterConnectionsNodes = []"><v-icon small>mdi-filter-remove</v-icon></v-btn>
                            </template>
                        </v-select>
                        <!-- <SelectWithGroups
                            :items="myItems"
                            :groups="myGroups"
                            label="Select multiple items"
                            v-model="selectedValues"
                            @change="handleSelectionChange"
                        ></SelectWithGroups> -->
                    </v-col>
                </v-row>
            </v-container>
        </v-card-text>

        <v-card-actions>
            <v-btn color="danger" outlined small @click="deleteWell">Удалить</v-btn>
            <v-spacer></v-spacer>
            <v-btn color="secondary" outlined small @click="recalcWell">Пересчитать</v-btn>
            <v-btn color="primary" elevation="0" @click="save">Сохранить</v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>

import NodeEdit from "@/components/Well/NodeEdit";
import SelectWithGroups from "@/components/SelectWithGroups";
import {Well, TYPE_PRODUCE, TYPE_INJECT, TYPE_CLOSED, TYPE_IMAGINARY, TYPE_AQUIFER} from "@/Well";
import {findNodeIndex, getConnectionsValue, getAllConnectionsText, getWellConnections} from "@/utils";

export default {
    components: {NodeEdit},
    props: ['value', 'reservoir', 'wells', 'nodes', 'connections', 'grid', 'inputConstants', 'recalcLoading'],
    emits: ['save', 'close'],
    data() {
        return {
            editedItem: this.value ? this.value.clone() : new Well({}, this.grid, this.inputConstants),
            types: [
                {text: 'Добывающая', value: TYPE_PRODUCE},
                {text: 'Нагнетающая', value: TYPE_INJECT},
                {text: 'Закрытая', value: TYPE_CLOSED},
                {text: 'Мнимая', value: TYPE_IMAGINARY},
                {text: 'Аквифер', value: TYPE_AQUIFER},
            ],
            filterConnectionsNodes: [],
        }
    },
    watch: {
        value() {
            this.editedItem = this.value ? this.value.clone() : new Well({}, this.grid, this.inputConstants);
            this.filterConnectionsNodes = [];
        },
    },
    methods: {
        addNode() {
           if (!this.editedItem.nodes) {
               this.editedItem.nodes = [];
           }

           let lastNode = this.editedItem.nodes.length > 0
                ? this.editedItem.nodes[this.editedItem.nodes.length - 1]
                : null;

           if (lastNode) {
               this.editedItem.addNode(lastNode.json());
           }
           else {
               this.editedItem.addNode({})
           }

        },
        joinNodes() {
            if (!this.editedItem.nodes) {
                return;
            }

            let avg = data => {
                let sum = data.reduce((aggr, item) => aggr + item, 0);
                return sum/data.length;
            }

            let joinCells = 5;

            let cellHeight = this.grid.cell_size[2] || 1;
            let joinLength = cellHeight * joinCells;

            let newNodes = [];
            let startDepth = null;
            let perforationLength = null;
            let currentNode = null;
            let joinedNodes = [];

            for (let nodeIndex in this.editedItem.nodes) {
                nodeIndex = parseInt(nodeIndex);
                let node = this.editedItem.nodes[nodeIndex];
                let isLastNode = nodeIndex === this.editedItem.nodes.length - 1;

                if (currentNode === null) {
                    currentNode = node.clone ? node.clone() : Object.assign({}, node);
                    startDepth = node.z_coord_relative;
                    perforationLength = node.l_perforation_length;
                    joinedNodes = [currentNode];
                    continue;
                }

                joinedNodes.push(node);
                if (node.z_coord_relative - startDepth >= joinLength || isLastNode) {
                    currentNode.l_perforation_length += node.z_coord_relative - startDepth;
                    currentNode.h_net_thickness = currentNode.l_perforation_length - cellHeight / 2;
                    currentNode.sw_water_saturation = avg(joinedNodes.map(joinedNode => joinedNode.sw_water_saturation || 0));
                    currentNode.phi_porosity = avg(joinedNodes.map(joinedNode => joinedNode.phi_porosity || 0));
                    currentNode.k_permeability = avg(joinedNodes.map(joinedNode => joinedNode.k_permeability || 0));
                    newNodes.push(currentNode);
                    currentNode = null;
                }
            }

            let wellConnections = getWellConnections(this.editedItem, this.connections, this.nodes);
            let updatedConnections = [];
            for (let newNode of newNodes) {
                let nodeIndex = findNodeIndex(newNode, this.editedItem.id, this.nodes);
                for (let connection of wellConnections) {
                    if (connection[0] === nodeIndex || connection[1] === nodeIndex) {
                        updatedConnections.push(connection);
                    }
                }
            }

            this.editedItem.nodes = newNodes;
            this.setConnections(updatedConnections);
        },
        close() {
            this.$emit('close');
        },
        save() {
            this.$emit('save', this.editedItem);
        },
        findNodeIndex(searchNode, wellId = null) {
            return findNodeIndex(searchNode, wellId, this.nodes);
        },
        getAllConnections(fromWell) {
            return getAllConnectionsText(fromWell, this.wells, this.nodes, this.filterConnectionsNodes);
        },
        getConnectionsValue(well) {
            return getConnectionsValue(well, this.connections, this.nodes, this.filterConnectionsNodes);
        },
        setConnections(newConnectionsValues) {
            let well = this.editedItem;
            let allConnections = getAllConnectionsText(well, this.wells, this.nodes);
            if (this.filterConnectionsNodes && this.filterConnectionsNodes.length > 0) {
                let excludedNodeIndexes = well.nodes
                    .map((node, nodeIndex) => nodeIndex)
                    .filter(nodeIndex => !this.filterConnectionsNodes.includes(nodeIndex));
                let excludedValues = getConnectionsValue(well, this.connections, this.nodes, excludedNodeIndexes);
                newConnectionsValues = [...excludedValues, ...newConnectionsValues];
            }

            let newConnections = newConnectionsValues
                .map(value => allConnections.find(item => item.value === value))
                .map(connectionObject => connectionObject ? connectionObject?.connection || null : null)
                .filter(connection => connection !== null);

            let nodeIndexes = this.editedItem.nodes
                .map(node => this.findNodeIndex(node, this.editedItem.id))
                .filter(index => index !== null);
            let noWellConnections = this.connections
                .filter(connection => {
                    return !nodeIndexes.includes(connection[0]) && !nodeIndexes.includes(connection[1]);
                });

            this.$emit('input-connections', noWellConnections.concat(newConnections));
        },
        deleteWell() {
            this.$emit('delete', this.editedItem);
        },
        recalcWell() {
            this.$emit('recalc', this.editedItem);
        },
    },
    computed: {
        editFormTitle() {
            return this.editedItem?.id
                ? 'Скважина '+this.editedItem?.id
                : 'Новая скважина';
        },
        editedItemZRelative() {
            return this.editedItem?.z_coord_relative ? parseFloat(this.editedItem?.z_coord_relative) : 0;
        },
        editedItemBottomholeDepth() {
            return this.editedItem?.bottomhole_depth ? parseFloat(this.editedItem?.bottomhole_depth) : 0;
        }
    }}
</script>

<style scoped>

</style>
